import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import maskGroupBg from "assets/images/others/attendee-side-images/Maskgroup.png";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import {
  ArrowRight,
  BookSvg,
  CorrectSvg,
  DropdwonClose,
  DropdwonOpen,
  InformationSvg,
} from "assets/images/svgs";
import AssessmentOne from "assets/images/others/attendee-side-images/assessment-1.png";
import { CORE_CONCEPT_TYPE } from "utils/helpers/enums/shared.enum";
import {
  LearningStyleDTO,
  SelectedAnswerDTO,
  SubmitBulkAnswersDTO,
} from "utils/helpers/models/attendee/learning-style.dto";
import useAttendee from "containers/attendee/useHooks";
import AcknowledgedPopup from "./AcknowledgedPopup";
import { siteRoutes } from "utils/helpers/enums/routes";
import useStore from "hooks/useStore";

const LearningSkills = () => {
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();
  const [minor, setMinor] = useState<MinorDTO>(new MinorDTO());
  const [selected, setSelected] = useState<CORE_CONCEPT_TYPE | null>(null);
  const [showAssessment, setShowAssessment] = useState(false);
  const [acknowledgedModalOpen, setAcknowledgedModalOpen] = useState<{
    open: boolean;
    type: string | null;
  }>({
    open: false,
    type: null,
  });

  const [learningSkillData, setLearningSkillData] = useState<
    LearningStyleDTO[]
  >([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswerDTO[]>(
    []
  );
  const { getLearningSkillsQuestions, postLearningStyleAnswers } =
    useAttendee();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const closeModals = () => {
    setAcknowledgedModalOpen({ open: false, type: null });
  };

  useEffect(() => {
    const savedMinor = localStorage.getItem("minorProfile");
    if (savedMinor) {
      setMinor(new MinorDTO(JSON.parse(savedMinor)));
    }
  }, []);

  const handleSelect = (value: CORE_CONCEPT_TYPE) => {
    setSelected(value);
  };

  useEffect(() => {
    const savedMinor = localStorage.getItem("minorProfile");
    if (savedMinor) {
      setMinor(new MinorDTO(JSON.parse(savedMinor)));
    }
  }, []);

  useEffect(() => {
    if (!selected) return;

    getLearningSkillsQuestions(
      (data: LearningStyleDTO[]) => {
        setLearningSkillData(data.map((d) => new LearningStyleDTO(d)));
        // Automatically show assessment once data is loaded
        if (data.length > 0) {
          setShowAssessment(true);
        }
      },
      { type: selected }
    );
  }, [selected]);

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  // Open first question on every step/page change
  useEffect(() => {
    setOpenIndex(0);
  }, [currentPage]);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleAnswerSelect = (
    questionId: number,
    optionId: number,
    points: number
  ) => {
    setSelectedAnswers((prev) => {
      const filtered = prev.filter(
        (answer) => answer.questionId !== questionId
      );
      return [
        ...filtered,
        new SelectedAnswerDTO({ questionId, optionId, points }),
      ];
    });
  };
  // ✅ Current step ke questions (ek style ke sare questions ek sath)
  const getCurrentQuestions = () => {
    return learningSkillData[currentPage]?.questions || [];
  };

  const getSelectedOption = (questionId: number) => {
    return selectedAnswers.find((answer) => answer.questionId === questionId);
  };

  const canGoNext = () => {
    const currentQuestions = getCurrentQuestions();
    return currentQuestions.every((question) =>
      selectedAnswers.some((answer) => answer.questionId === question.id)
    );
  };

  const handleNext = () => {
    if (currentPage < learningSkillData.length - 1) {
      setCurrentPage((prev) => prev + 1);
      setOpenIndex(0); // Reset to first question on new page
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    const payload = new SubmitBulkAnswersDTO({
      childId: minor.id,
      answers: selectedAnswers.map((a) => ({
        questionId: a.questionId,
        optionId: a.optionId,
      })),
      isForLearning: false,
    });

    postLearningStyleAnswers(payload, setMinor);
  };

  // ✅ Progress bar dynamic
  const totalSteps = learningSkillData.length;
  const progressPercentage =
    totalSteps > 0 ? ((currentPage + 1) / totalSteps) * 100 : 0;
  const isLastStep = currentPage === totalSteps - 1;
  const currentQuestions = getCurrentQuestions();

  return (
    <div className="">
      <div
        style={{ backgroundImage: `url(${maskGroupBg})` }}
        className="bg-cover bg-center bg-no-repeat h-full md:h-[200px] w-full"
      >
        <div className="flex justify-center items-center h-full px-4 py-8 md:py-0">
          <h1 className="font-semibold text-xl sm:text-2xl md:text-3xl text-blackie text-center">
            Learning skill Assessment
          </h1>
          {/* <p>
            {selected === CORE_CONCEPT_TYPE.APPROPRIATE_FOR_AGE_LEVEL
              ? "Advanced Learning Skills"
              : "Foundational Learning Skills"}
          </p> */}
        </div>
      </div>
      <div className="container md:mt-[-95px] px-4 sm:px-6">
        <div className="flex justify-between flex-wrap items-end gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 sm:gap-4">
            <img
              className="w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 object-cover border-4 border-white rounded-full"
              src={concatPathWithBackendUrl(minor?.profilePicUrl)}
              alt="profilePic"
            />
            <div>
              <span className="text-lg sm:text-xl md:text-2xl text-blackie font-semibold mb-8 flex flex-col sm:flex-row gap-1 sm:gap-2 items-start sm:items-end">
                {`${minor.firstName} ${minor.lastName}`}{" "}
                <p className="text-sm sm:text-base text-primaryBlueText">@{minor.email}</p>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-[70vh]">
        {!selected ? (
          <div className="container min-h-[70vh] px-4 sm:px-6 py-4 sm:py-6">
            <p className="text-primaryBlueText text-base sm:text-lg font-medium mb-4 sm:mb-6">
              To better tailor your assessment, select which of the 2 best
              describes your learner:
            </p>

            <div className="flex flex-col gap-4 w-full sm:w-3/4 md:w-1/2">
              <button
                onClick={() =>
                  handleSelect(CORE_CONCEPT_TYPE.APPROPRIATE_FOR_AGE_LEVEL)
                }
                className="p-3 sm:p-4 border border-borderColor rounded-xl min-h-[120px] sm:h-[150px] hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 h-full">
                  <CorrectSvg className="w-8 h-8 sm:min-w-10 sm:h-10 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl md:text-2xl text-left font-semibold text-primaryBlueText mb-1 sm:mb-2">
                      Advanced Learning Skills
                    </h3>
                    <p className="text-sm sm:text-base text-left text-primaryBlueText">
                      Designed for learners ready to engage in higher-level
                      thinking and problem-solving.
                    </p>
                  </div>
                  <ArrowRight className="w-6 h-6 sm:min-w-10 sm:h-10 flex-shrink-0 self-end sm:self-center" />
                </div>
              </button>
              <button
                onClick={() =>
                  handleSelect(CORE_CONCEPT_TYPE.EXCEPTION_LEARNER)
                }
                className="p-3 sm:p-4 border border-borderColor rounded-xl min-h-[120px] sm:h-[150px] hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 h-full">
                  <BookSvg className="w-8 h-8 sm:min-w-10 sm:h-10 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl md:text-2xl text-left font-semibold text-primaryBlueText mb-1 sm:mb-2">
                      Foundational Learning Skills
                    </h3>
                    <p className="text-sm sm:text-base text-left text-primaryBlueText">
                      Focused on the early stages of development, this assessment
                      supports learners in building essential skills across
                      domains.
                    </p>
                  </div>
                  <ArrowRight className="w-6 h-6 sm:min-w-10 sm:h-10 flex-shrink-0 self-end sm:self-center" />
                </div>
              </button>
            </div>
          </div>
        ) : learningSkillData.length > 0 && showAssessment ? (
          <div className="container w-full px-4 sm:px-6 md:px-8 lg:w-[calc(100%-130px)] flex flex-col items-center justify-center py-4 sm:py-6">
            <div className="w-full">
              {learningSkillData.length && (
                <h1 className="font-semibold text-lg sm:text-xl md:text-2xl text-blackie mb-4 sm:mb-6 text-center sm:text-left">
                  {learningSkillData[currentPage]?.name}
                </h1>
              )}
            </div>
            <div className="w-full mb-4 sm:mb-6">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                {Math.round(progressPercentage)}% - Step {currentPage + 1} of{" "}
                {totalSteps}
              </p>
            </div>

            <div className="w-full space-y-3 sm:space-y-4">
              {currentQuestions.map((question, idx) => {
                const selectedOption = getSelectedOption(question.id);
                const actualIndex = idx;

                return (
                  <div
                    key={question.id}
                    className={`${
                      selectedOption
                        ? "border border-[#2C4657] rounded-xl bg-fade shadow-sm"
                        : "border border-borderColor rounded-xl bg-fade shadow-sm"
                    } ${openIndex === actualIndex ? "bg-fade" : "bg-white"}`}
                  >
                    <button onClick={() => toggleQuestion(actualIndex)} className="w-full text-left px-3 sm:px-4 pt-3 sm:pt-4 pb-2 flex justify-between items-start cursor-default gap-2">
                      <div className="flex flex-col gap-1 sm:gap-2 flex-1 min-w-0">
                        <span className="font-medium text-sm sm:text-base text-primaryBlueText">
                          Does your child{" "}
                          {`${minor?.firstName} ${minor?.lastName}`}
                        </span>
                        <span className="text-sm sm:text-base text-primaryBlueText break-words">
                          {question?.title}
                        </span>
                      </div>

                      <span className="ml-2 cursor-pointer flex-shrink-0">
                        {openIndex === actualIndex ? (
                          <DropdwonClose />
                        ) : (
                          <DropdwonOpen />
                        )}
                      </span>
                    </button>

                    {openIndex === actualIndex && (
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2 sm:space-y-3">
                        {question.options
                          .slice()
                          .sort((a, b) => a.points - b.points)
                          .map((option) => (
                            <label
                              key={option.id}
                              className="flex items-start sm:items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name={`question-${question.id}`}
                                value={option.id}
                                checked={selectedOption?.optionId === option.id}
                                onChange={() =>
                                  handleAnswerSelect(
                                    question.id,
                                    option.id,
                                    option.points
                                  )
                                }
                                className="custom-radio-question w-4 h-4 mt-1 sm:mt-0 flex-shrink-0"
                              />
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 flex-1 min-w-0">
                                <span className="text-sm sm:text-base text-primaryBlueText break-words">
                                  {option.title}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  ({option.points} points)
                                </span>
                              </div>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="w-full flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-4 sm:mt-6">
              <button
                onClick={handleBack}
                disabled={currentPage === 0}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 rounded-full text-sm sm:text-base ${
                  currentPage === 0
                    ? "bg-fade text-gray-500 cursor-not-allowed"
                    : "bg-yellowText text-white"
                }`}
              >
                Back
              </button>

              {isLastStep ? (
                <button
                  onClick={handleSubmit}
                  disabled={!canGoNext()}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 rounded-full text-sm sm:text-base ${
                    canGoNext()
                      ? "bg-primaryBlueBg text-white"
                      : "bg-fade text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Submit
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canGoNext()}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 rounded-full text-sm sm:text-base ${
                    canGoNext()
                      ? "bg-primaryBlueBg text-white "
                      : "bg-fade text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        ) : selected && learningSkillData.length === 0 ? (
          <div className="container min-h-[70vh] flex items-center justify-center px-4">
            <p className="text-primaryBlueText text-base sm:text-lg">Loading assessment...</p>
          </div>
        ) : null}
      </div>
      {acknowledgedModalOpen && (
        <AcknowledgedPopup
          isOpen={acknowledgedModalOpen.open} // ✅ sirf boolean
          onClose={() => setAcknowledgedModalOpen({ open: false, type: null })}
          selected={acknowledgedModalOpen.type}
        />
      )}
    </div>
  );
};

export default LearningSkills;
